import { useState, useEffect } from "react";
import { Wallet, ShieldAlert } from "lucide-react";
import { fmtUsd, fmtNum } from "@/lib/api";
import { cn } from "@/lib/utils";

const SlPendingBadge = ({ p }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!p.sl_breach_since) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [p.sl_breach_since]);
  if (!p.sl_breach_since) return null;
  const started = new Date(p.sl_breach_since).getTime();
  const total = p.sl_confirm_seconds ?? 120;
  const remaining = Math.max(0, Math.ceil(total - (now - started) / 1000));
  return (
    <span data-testid={`sl-pending-${p.symbol}`}
      className="ml-1.5 inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FFB020]/15 text-[var(--warn)] border border-[#FFB020]/30 align-middle">
      <ShieldAlert className="h-2.5 w-2.5" /> SL ONAYI {remaining}sn
    </span>
  );
};

export const PositionsTable = ({ positions }) => {
  return (
    <div data-testid="positions-table" className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)]">
      <div className="flex items-center gap-2 px-5 h-14 border-b border-[var(--border)]">
        <Wallet className="h-4 w-4 text-[var(--pos)]" />
        <h3 className="font-head font-bold text-base">Açık Pozisyonlar</h3>
      </div>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] border-b border-[var(--surface-hover)]">
              <th className="text-left font-normal py-2.5 px-5">Varlık</th>
              <th className="text-right font-normal py-2.5 px-3">Miktar</th>
              <th className="text-right font-normal py-2.5 px-3">Giriş</th>
              <th className="text-right font-normal py-2.5 px-3">Güncel</th>
              <th className="text-right font-normal py-2.5 px-3">Stop-Loss</th>
              <th className="text-right font-normal py-2.5 px-3">Değer</th>
              <th className="text-right font-normal py-2.5 px-5">K/Z</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {positions.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-[var(--text-faint)] text-sm font-sans">Açık pozisyon yok</td></tr>
            )}
            {positions.map((p) => {
              const pos = p.unrealized_pnl >= 0;
              return (
                <tr key={p.symbol} data-testid={`position-row-${p.symbol}`} className="border-b border-[var(--surface-hover)] hover:bg-[var(--surface-hover)] transition-colors">
                  <td className="py-3 px-5 font-head font-bold">
                    {p.symbol}
                    {p.risk_mode && <span className="ml-1.5 text-[9px] text-[var(--accent)] uppercase">BOT</span>}
                    {p.live && <span className="ml-1.5 text-[9px] font-bold text-[var(--neg)] uppercase">CANLI</span>}
                    <SlPendingBadge p={p} />
                  </td>
                  <td className="py-3 px-3 text-right text-[var(--text-dim)]">{fmtNum(p.qty, 6)}</td>
                  <td className="py-3 px-3 text-right text-[var(--text-dim)]">{fmtUsd(p.avg_price)}</td>
                  <td className="py-3 px-3 text-right">{fmtUsd(p.current_price)}</td>
                  <td className="py-3 px-3 text-right text-[var(--neg)]">{p.sl_price ? fmtUsd(p.sl_price) : "--"}</td>
                  <td className="py-3 px-3 text-right">{fmtUsd(p.market_value)}</td>
                  <td className={cn("py-3 px-5 text-right font-bold", pos ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
                    {pos ? "+" : ""}{fmtUsd(p.unrealized_pnl)}
                    <div className="text-[10px] font-normal">{pos ? "+" : ""}{p.unrealized_pnl_pct}%</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-[var(--border)]">
        {positions.length === 0 && (
          <p className="text-center py-8 text-[var(--text-faint)] text-sm">Açık pozisyon yok</p>
        )}
        {positions.map((p) => {
          const pos = p.unrealized_pnl >= 0;
          return (
            <div key={p.symbol} data-testid={`position-card-${p.symbol}`} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-head font-bold text-base">
                  {p.symbol}
                  {p.risk_mode && <span className="ml-1.5 text-[9px] text-[var(--accent)] uppercase">BOT</span>}
                  {p.live && <span className="ml-1.5 text-[9px] font-bold text-[var(--neg)] uppercase">CANLI</span>}
                  <SlPendingBadge p={p} />
                </div>
                <div className={cn("text-right font-mono font-bold", pos ? "text-[var(--pos)]" : "text-[var(--neg)]")}>
                  {pos ? "+" : ""}{fmtUsd(p.unrealized_pnl)}
                  <div className="text-[10px] font-normal">{pos ? "+" : ""}{p.unrealized_pnl_pct}%</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-xs font-mono">
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Miktar</span><span className="text-[var(--text-dim)]">{fmtNum(p.qty, 6)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Değer</span><span>{fmtUsd(p.market_value)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Giriş</span><span className="text-[var(--text-dim)]">{fmtUsd(p.avg_price)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Güncel</span><span>{fmtUsd(p.current_price)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-faint)]">Stop-Loss</span><span className="text-[var(--neg)]">{p.sl_price ? fmtUsd(p.sl_price) : "--"}</span></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
